import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout, HorizontalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold, TitleNormal } from "../../common/common-text";
import { useState } from "react";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import BTEditor from "../../common/components/board/BTEditor";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import { dialogStore } from "../../store/DialogStore";
import {
  requestDeleteFile,
  requestUploadFiles,
  requestUpdateMarketingDocuments,
} from "../../service/BoardService";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";

const MarketingBoardEdit = () => (
  <MainLayout>
    <MarketingBoardEditBody />
  </MainLayout>
);

export default MarketingBoardEdit;

const MarketingBoardEditBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const marketing = location.state?.marketing;
  const marketingTypeOptions = location.state?.marketingTypeOptions || [];

  const [title, setTitle] = useState(marketing?.title || "");
  const [contents, setContents] = useState(marketing?.contents || "");
  const [selectedImage, setSelectedImage] = useState(marketing?.representImage || null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>(marketing?.attachedFiles || []);
  const [marketingType, setMarketingType] = useState(marketing?.marketingType || "");
  const [postDate, setPostDate] = useState(marketing?.postDate || "");
  const [isPublished, setIsPublished] = useState(marketing?.isPublished ?? true);

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, "text/html");
      const imgElements = docParsed.querySelectorAll("img");
      const docId = marketing.id;

      // 1) 현재 본문에서 data: 이미지 수집
      const base64ImageSrcs: string[] = [];
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src?.startsWith("data:")) base64ImageSrcs.push(src);
      });

      // 2) base64 → File 변환
      const imagesToUpload: File[] = [];
      for (const b64 of base64ImageSrcs) {
        const res = await fetch(b64);
        const blob = await res.blob();
        imagesToUpload.push(new File([blob], `${Date.now()}_img.png`, { type: blob.type }));
      }

      // 3) 업로드 & 매핑 구성 (data: → remote)
      const uploadedImageUrls = imagesToUpload.length
        ? await requestUploadFiles("marketing", docId, "image", imagesToUpload)
        : [];

      const base64ToRemote = new Map<string, string>();
      base64ImageSrcs.forEach((b64, i) => {
        if (uploadedImageUrls[i]) base64ToRemote.set(b64, uploadedImageUrls[i]);
      });

      // 4) 본문 내 src 치환 (data: → remote)
      let idx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src?.startsWith("data:")) {
          const newUrl = uploadedImageUrls[idx++];
          if (newUrl) img.setAttribute("src", newUrl);
        }
      });

      // 5) 이전 본문과 비교하여 제거된 원격 이미지 삭제
      const prevDocParsed = parser.parseFromString(marketing.contents || "", "text/html");
      const prevHttpImgs = Array.from(prevDocParsed.querySelectorAll("img"))
        .map((img) => img.getAttribute("src"))
        .filter((src): src is string => !!src && src.startsWith("http"));

      const currHttpImgs = Array.from(docParsed.querySelectorAll("img"))
        .map((img) => img.getAttribute("src"))
        .filter((src): src is string => !!src && src.startsWith("http"));

      const deletedImgUrls = prevHttpImgs.filter((url) => !currHttpImgs.includes(url));
      await Promise.all(deletedImgUrls.map(async (url) => {
        const m = url.match(/\/o\/([^?]+)\?/);
        if (m) await requestDeleteFile(decodeURIComponent(m[1]));
      }));

      // 6) 첨부파일(파일탭) 변경사항 반영
      const prevAttachedFiles: string[] = Array.isArray(marketing.attachedFiles) ? marketing.attachedFiles : [];
      const keepSet = new Set(uploadedFileUrls);
      const removedAttachments = prevAttachedFiles.filter((u) => !keepSet.has(u));

      await Promise.all(removedAttachments.map(async (url) => {
        const m = url.match(/\/o\/([^?]+)\?/);
        if (m) await requestDeleteFile(decodeURIComponent(m[1]));
      }));

      let newAttachmentUrls: string[] = [];
      if (uploadedFiles.length > 0) {
        newAttachmentUrls = await requestUploadFiles("marketing", docId, "attachments", uploadedFiles);
      }
      const finalAttachedFiles = [
        ...uploadedFileUrls.filter((u) => prevAttachedFiles.includes(u)),
        ...newAttachmentUrls,
      ];

      // 7) 대표 이미지 결정 (state 의존 X, 매핑 우선)
      let representUrl: string | null = null;

      if (selectedImage) {
        if (selectedImage.startsWith("data:")) {
          representUrl = base64ToRemote.get(selectedImage) ?? null; // 업로드된 원격 URL
        } else {
          representUrl = selectedImage; // 이미 원격 URL
        }
      }
      // 폴백: 본문 첫 번째 이미지(이미 치환 완료 상태)
      if (!representUrl) {
        representUrl = docParsed.querySelector("img")?.getAttribute("src") ?? marketing.representImage ?? null;
      }

      // 8) 최종 저장
      marketing.title = title;
      
      // docParsed: DOMParser로 파싱한 문서
      const imgElements2 = docParsed.querySelectorAll("img");

      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });

      // 최종적으로 contents에 반영
      marketing.contents = docParsed.body.innerHTML;
      
      marketing.representImage = representUrl;
      marketing.marketingType = marketingType;
      marketing.postDate = postDate ? new Date(postDate) : marketing.postDate;
      marketing.isPublished = isPublished;
      marketing.attachedFiles = finalAttachedFiles;
      marketing.updatedBy = authStore.user?.username ?? "";
      marketing.updatedAt = new Date();

      await requestUpdateMarketingDocuments(marketing);

      dialogStore.showDialog({
        title: "성공",
        message: "홍보자료가 성공적으로 수정되었습니다.",
        onConfirm: () =>
          navigate("/board/marketing/detail", { state: { marketing, marketingTypeOptions } }),
        onClose: () =>
          navigate("/board/marketing/detail", { state: { marketing, marketingTypeOptions } }),
      });
    } catch (e) {
      console.error("수정 실패:", e);
      dialogStore.showDialog({
        title: "오류",
        message: "수정 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
        <TitleLargeMedium>홍보자료 수정</TitleLargeMedium>
        <BTBox height="24px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        <VerticalLayout height={'auto'} paddingBottom={36}>
            <TitleMediumBold style={{ marginTop: "20px" }}>홍보 자료 정보</TitleMediumBold>
            <BTBox height="16px" />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "nowrap",            // 줄바꿈 방지
                    fontSize: "14px",
                    color: "#6C757D",
                }}
            >
                <b style={{ whiteSpace: "nowrap" }}>홍보 유형</b>
                {/* select / date가 100% 먹지 않도록 래퍼로 폭 제어 */}
                <div style={{ width: 260, flex: "0 0 auto" }}>
                    <BTSelectBox
                        width="100%"
                        options={marketingTypeOptions}
                        value={marketingType}
                        onChange={(e) => setMarketingType(e.target.value)}
                    />
                </div>
            </div>

            <BTBox height="16px" />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "nowrap",            // 줄바꿈 방지
                    fontSize: "14px",
                    color: "#6C757D",
                }}
            >
                <b style={{ whiteSpace: "nowrap" }}>게시일자</b>
                <BTDateComponent
                    width="100%"
                    value={postDate}
                    onChange={(value) => setPostDate(value)}
                />
                <Divider
                    orientation="vertical"
                    sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }}
                />
                <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
                <BTCheckBox
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                />
            </div>
            <BTBox height='16px' />
            <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />
            <TitleMediumBold style={{ marginTop: "20px" }}>홍보자료 컨텐츠 입력</TitleMediumBold>
            <BTBox height='16px' />
            <BTEditor
              title={title}
              contents={contents}
              selectedImage={selectedImage}
              uploadedFiles={uploadedFiles}
              uploadedFileUrls={uploadedFileUrls}
              setTitle={setTitle}
              setContents={setContents}
              setSelectedImage={setSelectedImage}
              setUploadedFiles={setUploadedFiles}
              setUploadedFileUrls={setUploadedFileUrls}
              onSave={handleSave}
              onCancel={() => navigate(-1)}
              attachmentAccept="all"
            />
        </VerticalLayout>
    </VerticalLayout>
  );
});